import type { RestoreJob, RestoreValidationResult, RestoreTriggerRequest } from "./types";

const getHeaders = () => {
  const token = localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken");
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
};

export const fetchRestoreJobs = async (): Promise<RestoreJob[]> => {
  const res = await fetch("/api/system/restore/jobs/", { headers: getHeaders() });
  if (!res.ok) {
    throw new Error(`Failed to fetch restore jobs: HTTP ${res.status}`);
  }
  const data = await res.json();
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data.results)) {
    return data.results;
  }
  return [];
};

export const validateBackupArchive = async (payload: {
  backup_id?: string;
  file_path?: string;
  r2_key?: string;
}): Promise<RestoreValidationResult> => {
  try {
    const res = await fetch("/api/system/restore/validate/", {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (res.ok && data.success) {
      return {
        success: true,
        message: data.message || "Archive structure and header validation passed.",
        details: data.details || {},
      };
    }
    return {
      success: false,
      error: data.error || data.detail || "Validation failed.",
      details: data.details || {},
    };
  } catch (err: any) {
    return {
      success: false,
      error: err?.message || "Network error during backup archive validation.",
    };
  }
};

export const triggerRestoreJob = async (
  req: RestoreTriggerRequest
): Promise<{ success: boolean; message: string; mode?: string; status?: string; error?: string }> => {
  try {
    const res = await fetch("/api/system/restore/trigger/", {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(req),
    });
    const data = await res.json();
    if (res.ok && data.success) {
      return {
        success: true,
        message: data.message || "Database restoration job triggered.",
        mode: data.mode,
        status: data.status,
      };
    }
    return {
      success: false,
      message: data.error || data.detail || "Failed to trigger restore job.",
      error: data.error || data.detail,
    };
  } catch (err: any) {
    return {
      success: false,
      message: err?.message || "Network error while triggering restore job.",
      error: err?.message,
    };
  }
};
